import { useQuery, useMutation } from '@tanstack/react-query';
import { billingApi } from '../lib/billing-api';
import type {
  CreditBalance,
  CreditPackage,
  PaymentRecord,
  StripeCheckoutResult,
  SolanaPaymentResult,
  SolanaVerifyResult,
} from '../lib/billing-api';
import { MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

// ── Mock data ────────────────────────────────────────────────────────────────

const MOCK_BALANCE: CreditBalance = {
  paidCredits: 4_200,
  freeCreditsUsed: 743,
  freeCreditsLimit: 1_000,
  totalCallsThisPeriod: 4_943,
};

const MOCK_PACKAGES: CreditPackage[] = [
  {
    id: 'pkg-1k',
    credits: 1_000,
    priceUsd: 1.0,
    originalPriceUsd: 1.0,
    discountPercent: 0,
    label: 'Starter',
  },
  {
    id: 'pkg-5k',
    credits: 5_000,
    priceUsd: 4.5,
    originalPriceUsd: 5.0,
    discountPercent: 10,
    label: 'Growth',
  },
  {
    id: 'pkg-10k',
    credits: 10_000,
    priceUsd: 8.0,
    originalPriceUsd: 10.0,
    discountPercent: 20,
    label: 'Scale',
  },
  {
    id: 'pkg-50k',
    credits: 50_000,
    priceUsd: 35.0,
    originalPriceUsd: 50.0,
    discountPercent: 30,
    label: 'Pro',
  },
];

const MOCK_HISTORY: PaymentRecord[] = [
  {
    id: 'pay-1',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    amountUsd: 8.0,
    credits: 10_000,
    provider: 'stripe',
    status: 'completed',
  },
  {
    id: 'pay-2',
    date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    amountUsd: 4.5,
    credits: 5_000,
    provider: 'solana',
    status: 'completed',
  },
];

// ── Query keys ────────────────────────────────────────────────────────────────

export const billingKeys = {
  all: ['billing'] as const,
  balance: (projectId: string) => [...billingKeys.all, 'balance', projectId] as const,
  packages: () => [...billingKeys.all, 'packages'] as const,
  history: (projectId: string) => [...billingKeys.all, 'history', projectId] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useBillingBalance(): ReturnType<typeof useQuery<CreditBalance>> {
  const { projectId } = useCurrentProject();

  return useQuery<CreditBalance>({
    queryKey: billingKeys.balance(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE || !projectId) return MOCK_BALANCE;
      return billingApi.getBalance(projectId);
    },
    enabled: true,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreditPackages(): ReturnType<typeof useQuery<CreditPackage[]>> {
  return useQuery<CreditPackage[]>({
    queryKey: billingKeys.packages(),
    queryFn: async () => {
      if (MOCK_MODE) return MOCK_PACKAGES;
      return billingApi.getPackages();
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function usePaymentHistory(): ReturnType<typeof useQuery<PaymentRecord[]>> {
  const { projectId } = useCurrentProject();

  return useQuery<PaymentRecord[]>({
    queryKey: billingKeys.history(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE || !projectId) return MOCK_HISTORY;
      return billingApi.getPaymentHistory(projectId);
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
}

export function useStripeCheckout(): ReturnType<
  typeof useMutation<StripeCheckoutResult, Error, string>
> {
  const { projectId } = useCurrentProject();

  return useMutation<StripeCheckoutResult, Error, string>({
    mutationFn: async (packageId: string) => {
      if (MOCK_MODE || !projectId) {
        return { url: 'https://checkout.stripe.com/pay/mock' };
      }
      return billingApi.createStripeCheckout(projectId, packageId);
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export function useSolanaPayment(): ReturnType<
  typeof useMutation<SolanaPaymentResult, Error, string>
> {
  const { projectId } = useCurrentProject();

  return useMutation<SolanaPaymentResult, Error, string>({
    mutationFn: async (packageId: string) => {
      if (MOCK_MODE || !projectId) {
        return {
          reference: `ref_mock_${Date.now()}`,
          recipient: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU',
          amount: 8.0,
          label: 'Ponzinomics Credits',
          solanaPay:
            'solana:7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU?amount=8&spl-token=EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
        };
      }
      return billingApi.createSolanaPayment(projectId, packageId);
    },
  });
}

export function useVerifySolanaPayment(): ReturnType<
  typeof useMutation<SolanaVerifyResult, Error, string>
> {
  return useMutation<SolanaVerifyResult, Error, string>({
    mutationFn: async (reference: string) => {
      if (MOCK_MODE) return { verified: true, credits: 10_000 };
      return billingApi.verifySolanaPayment(reference);
    },
  });
}
